import { Component, computed, inject, input, resource, signal } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { EhrService, EhrStatus, EhrStatusPipe, TypeOfHospitalizationPipe } from '@beautyapp/core';
import { InputModule } from '@beautyapp/form';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { rxResource } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  selector: 'beautyapp-user-ehr',
  imports: [
    InputModule,
    NgxPermissionsModule,
    NzGridModule,
    NzTableModule,
    ReactiveFormsModule,
    DatePipe,
    NzButtonModule,
    NzIconModule,
    NzPopconfirmModule,
    EhrStatusPipe,
    TypeOfHospitalizationPipe,
    NgClass,
    NzTooltipDirective,
    NzDropDownModule,
    NzMenuModule,
    NzSwitchModule,
    FormsModule,
    TranslateModule
],
  templateUrl: './user-ehr.component.html',
  styleUrl: './user-ehr.component.scss'
})
export class UserEhrComponent {
  ehrService = inject(EhrService);

  userId = input.required<string>();

  loadData: any[] = [];
  loading = false;
  page = signal(1);
  pageSize = signal(10);
  onlyWithIntervention = signal(false);

  ehrResources = rxResource({
    params: () => ({
      page: this.page(),
      pageSize: this.pageSize(),
      userId: this.userId(),
      onlyWithIntervention: this.onlyWithIntervention() || undefined
    }),
    stream: ({ params }) => this.ehrService.getPagination({
      page: params.page,
      pageSize: params.pageSize,
      userId: params.userId,
      onlyWithIntervention: params.onlyWithIntervention
    })
  });

  data = computed(() => this.ehrResources.value()?.data || []);

  total = computed(() => this.ehrResources.value()?.meta?.itemCount || 0);

  statusClass = computed(
    () => (status: EhrStatus) => {
      switch (status) {
        case EhrStatus.Pending:
          return 'text-yellow-600 text-yellow-600 border border-yellow-400 bg-yellow-50';
        case EhrStatus.Accepted:
          return 'text-blue-500 border border-blue-300 bg-blue-50';
        case EhrStatus.Rejected:
        case EhrStatus.Cancelled:
          return 'text-red-500 border border-red-300 bg-red-50';
        case EhrStatus.Sent:
          return 'text-green-500 border border-green-300 bg-green-50';
        case EhrStatus.Unknown:
          return 'text-gray-500 border border-gray-300 bg-gray-50';
        default:
          return '';
      }
    });

  paginatorChange($event: NzTableQueryParams) {
    this.page.set($event.pageIndex);
    this.pageSize.set($event.pageSize);
  }

  toggleOnlyWithIntervention(value: boolean) {
    this.onlyWithIntervention.set(value);
    this.page.set(1);
  }

  openAmbulatoryCard(item: any) {
    if (!item?.id) return;
    window.open(`/ehealth/ambulatory-card/${item.id}`, '_blank');
  }

  openSmallAmbulatoryCard(item: any) {
    if (!item?.id) return;
    window.open(`/ehealth/small-ambulatory-card/${item.id}`, '_blank');
  }

  openObjectiveExaminations(item: any) {
    if (!item?.id) return;
    window.open(`/ehealth/objective-examinations/${item.id}`, '_blank');
  }

  openMedicalInterventionProtocol(item: any) {
    if (!item?.id) return;
    window.open(`/ehealth/medical-intervention-protocol/${item.id}`, '_blank');
  }

  printForm100(item: any) {
    if (!item?.id) return;
    this.ehrService
      .generateAndSaveForm100([item.id], item.userId, item.employeeId)
      .pipe(untilDestroyed(this))
      .subscribe((res: any) => {
        if (res?.url) {
          window.open(res.url, '_blank');
        }
      });
  }

  printPrescription(item: any) {
    if (!item?.id) return;
    this.ehrService
      .generateAndSavePrescriptionInline(item.id)
      .pipe(untilDestroyed(this))
      .subscribe((res: any) => {
        if (res?.url) {
          window.open(res.url, '_blank');
        }
      });
  }

  printInformedConsent(item: any) {
    if (!item?.id) return;
    this.ehrService
      .generateAndSaveForm2008(item.id)
      .pipe(untilDestroyed(this))
      .subscribe((res: any) => {
        if (res?.url) {
          window.open(res.url, '_blank');
        }
      });
  }
}
